import React, { useState } from 'react';

import { faPaperclip, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';

import { Button, ControlLabel, Text } from '@shared/components/bootstrap';
import { Manager, Selector, Uploader } from '@shared/components/files';
import { useLegacyAttachmentUpload } from '@shared/hooks';

import { DateTimePicker } from '@admin/components/helpers/date_time_picker';
import { Panel } from '@admin/components/helpers/panel';
import { Files } from '@admin/components/notes/files';
import { Categories } from '@admin/components/tickets/categories';
import { TicketGroupPicker } from '@admin/components/tickets/ticket_group_picker';
import { newWarehouseTaskURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import {
  Ticket,
  TicketQuery,
  TicketFragment,
  Ticket__TicketableEnum,
  Ticket__TicketableInput,
  TicketsDocument,
  useTicketCreateMutation,
  useTicketUpdateMutation,
} from '@admin/schema';

const ACCEPT = 'image/*,application/pdf';

type TicketFormProps = {
  onClose(): void;
  embedded?: boolean; // When true, renders without Panel wrapper (for use in modals)
} & (
  | { ticket: Ticket | TicketQuery['ticket'] | TicketFragment; ticketable?: never } // Edit mode: ticket required, ticketable not allowed
  | { ticket?: never; ticketable: Ticket__TicketableInput & { ticketableName?: string } } // Create mode: ticketable required, ticket not allowed
);

export const TicketForm: React.FC<TicketFormProps> = ({ ticket, ticketable, onClose, embedded = false }) => {
  const [category, setCategory] = useState<string>(ticket?.category || '');
  const [subCategory, setSubCategory] = useState<string | undefined>(ticket?.subCategory || undefined);
  const [description, setDescription] = useState<string>(ticket?.description || '');
  const [businessUnit, setBusinessUnit] = useState<string>(ticket?.businessUnit || '');
  const [area, setArea] = useState<string>(ticket?.area || '');
  const [assignedToLead, setAssignedToLead] = useState<boolean>(false);
  const [groupID, setGroupID] = useState<string | undefined>(ticket?.group?.id);
  const [assigneeID, setAssigneeID] = useState<string | undefined>(ticket?.assignee?.id);
  const [notify, setNotify] = useState<boolean>(ticket?.notify ?? true);
  const [liveNow, setLiveNow] = useState<boolean>(
    ticket?.goLiveTime ? DateTime.fromISO(ticket.goLiveTime) <= DateTime.now() : true,
  );
  const [goLiveTime, setGoLiveTime] = useState<DateTime | undefined>(
    ticket?.goLiveTime ? DateTime.fromISO(ticket.goLiveTime) : undefined,
  );
  const [dueDate, setDueDate] = useState<DateTime | undefined>(
    ticket?.dueDate ? DateTime.fromISO(ticket.dueDate) : DateTime.now().plus({ hours: 48 }),
  );
  // Key to force DateTimePicker remount when we programmatically update dueDate
  const [dueDateKey, setDueDateKey] = useState(0);

  // Handler for schedule changes - updates due date to 48 hours from schedule time (new tickets only)
  const onLiveNowChange = (nowSelected: boolean) => {
    setLiveNow(nowSelected);
    if (!ticket) {
      if (nowSelected) {
        // Switching to "Now" - set due date to 48 hours from now
        setDueDate(DateTime.now().plus({ hours: 48 }));
        setDueDateKey((k) => k + 1);
      } else if (goLiveTime) {
        // Switching to "At" with existing goLiveTime - set due date to 48 hours from that time
        setDueDate(goLiveTime.plus({ hours: 48 }));
        setDueDateKey((k) => k + 1);
      }
    }
  };

  const onGoLiveTimeChange = (newGoLiveTime: DateTime | undefined) => {
    setGoLiveTime(newGoLiveTime);
    if (!ticket && newGoLiveTime) {
      // Schedule time changed - set due date to 48 hours from that time
      setDueDate(newGoLiveTime.plus({ hours: 48 }));
      setDueDateKey((k) => k + 1);
    }
  };

  const [uploaded, setUploaded] = useState<
    Array<{
      file: File;
      url: string;
    }>
  >([]);

  const [formError, setFormError] = useState<string | undefined>();

  const [ticketCreate, { loading: createLoading }] = useTicketCreateMutation({
    client,
    refetchQueries: ticketable
      ? [
          {
            query: TicketsDocument,
            variables: {
              ticketable: {
                id: ticketable.id,
                type: ticketable.type,
                associatedLeadUserID: ticketable.associatedLeadUserID,
              },
            },
          },
        ]
      : [],
  });

  const [ticketUpdate, { loading: updateLoading }] = useTicketUpdateMutation({
    client,
  });

  const loading = createLoading || updateLoading;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const inputFields = {
      ticketableType: ticket?.ticketableType || ticketable?.type,
      ticketableID: ticket?.ticketableID
        ? String(ticket.ticketableID)
        : ticketable?.id
          ? String(ticketable.id)
          : undefined,
      groupID,
      assigneeID,
      description,
      category,
      subCategory,
      businessUnit: businessUnit,
      area: area,
      liveNow,
      goLiveTime: goLiveTime?.toISO(),
      dueDate: dueDate?.toISO(),
      notify,
      imagesAttributes: uploaded.map(({ url }) => ({ url })),
    };

    try {
      let response;
      if (ticket) {
        response = await ticketUpdate({
          variables: {
            id: ticket.id,
            ticketInput: inputFields,
          },
        });
        if (response.data?.ticketUpdate.error) {
          setFormError(response.data.ticketUpdate.error);
          return;
        }
      } else {
        response = await ticketCreate({
          variables: {
            input: inputFields,
          },
        });
        if (response.data?.ticketCreate.error) {
          setFormError(response.data.ticketCreate.error);
          return;
        }
      }

      // Success callback
      if (ticket) {
        // For updates, just close or stay on the page
        onClose();
      } else {
        // For new tickets, navigate to the ticket view page
        const ticketID = response.data && 'ticketCreate' in response.data && response.data.ticketCreate?.ticket?.id;
        if (ticketID) {
          window.location.href = `/tickets/${ticketID}`;
        } else {
          onClose();
        }
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const onCategoryChange = (newCategory: string, newSubCategory: string) => {
    setCategory(newCategory);
    setSubCategory(newSubCategory);
  };

  const onTicketGroupChange = (newGroupID: string, newAssigneeID: string) => {
    setGroupID(newGroupID);
    setAssigneeID(newAssigneeID);
  };

  const onUploaded = (file: File, url: string) => {
    setUploaded((current) => [...current, { file, url }]);
  };

  const onAssignToLeadChange = (newValue: boolean) => {
    setAssignedToLead(newValue);

    if (newValue && ticketable?.associatedLeadUserID) {
      setGroupID(undefined);
      setAssigneeID(ticketable.associatedLeadUserID);
    }
  };

  const showAssignToLeadCheckbox = !!ticketable?.associatedLeadUserID;
  const showTicketGroupSelector = !assignedToLead;

  const formContent = (
    <>
      {!ticket && (
        <div className="alert alert-info" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <strong>Looking to create a warehouse task?</strong> For inventory tasks like reshoots, storage checks, item
            details, and more, use the new Warehouse Tasks workflow.
          </div>
          <a
            href={newWarehouseTaskURL(
              ticketable?.type === Ticket__TicketableEnum.Account ? { accountID: ticketable.id } : undefined,
            )}
            className="btn btn-primary btn-sm"
          >
            Go to Warehouse Tasks
          </a>
        </div>
      )}
      {/* Subject field - always disabled */}
      <div className="form-group">
        <ControlLabel>
          <strong>Subject</strong>
        </ControlLabel>
        <input
          type="text"
          className="form-control"
          value={
            ticket
              ? ticket.ticketableName
              : ticketable
                ? ticketable.ticketableName || `${ticketable.type} #${ticketable.id}`
                : ''
          }
          disabled
        />
      </div>

      {/* Categories - renders two dropdowns side by side */}
      <div className="form-group mar-top">
        <ControlLabel>
          <strong>Category</strong>
        </ControlLabel>
        <div className="row">
          <Categories
            className="col-sm-6"
            selectedCategory={category}
            selectedSubCategory={subCategory}
            onChange={onCategoryChange}
          />
        </div>
      </div>

      {/* Full-width description */}
      <div className="form-group mar-top">
        <ControlLabel>
          <strong>Description</strong>
        </ControlLabel>
        <textarea
          required
          id="ticket-description"
          className="form-control"
          rows={4}
          value={description}
          placeholder="Please change the plan for this account"
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      {/* Business Unit and Area in 2-column layout */}
      <div className="form-group mar-top">
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <ControlLabel>
                <strong>Business Unit</strong>
              </ControlLabel>
              <select className="form-control" value={businessUnit} onChange={(e) => setBusinessUnit(e.target.value)}>
                <option value="">Select Business Unit</option>
                <option value="Moving">Moving</option>
                <option value="Smart Storage">Smart Storage</option>
                <option value="Self Storage">Self Storage</option>
                <option value="Logistics">Logistics</option>
                <option value="Engineering">Engineering</option>
                <option value="Operations">Operations</option>
              </select>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <ControlLabel>
                <strong>Area</strong>
              </ControlLabel>
              <select className="form-control" value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">Select Area</option>
                <option value="Operations">Operations</option>
                <option value="Customer Service">Customer Service</option>
                <option value="Logistics">Logistics</option>
                <option value="Engineering">Engineering</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Assign to Lead checkbox */}
      {showAssignToLeadCheckbox && (
        <div className="form-group mar-top">
          <ControlLabel>
            <input
              id="ticket-assign-to-lead"
              type="checkbox"
              onChange={(event) => onAssignToLeadChange(event.target.checked)}
              checked={assignedToLead}
            />{' '}
            <strong>Assign to Lead</strong>
          </ControlLabel>
        </div>
      )}

      {/* Group and Assignee - renders two dropdowns side by side */}
      {showTicketGroupSelector && (
        <div className="form-group mar-top">
          <ControlLabel>
            <strong>Assign to</strong>
          </ControlLabel>
          <div className="row">
            <TicketGroupPicker
              className="col-sm-6"
              selectedGroupID={groupID}
              selectedAssigneeID={assigneeID}
              onChange={onTicketGroupChange}
            />
          </div>
        </div>
      )}

      {/* Schedule/Reschedule section */}
      <div className="form-group mar-top">
        <ControlLabel>
          <strong>{ticket ? 'Reschedule' : 'Schedule'}</strong>
        </ControlLabel>
        <div className="row">
          <div className="col-sm-2">
            <ControlLabel>
              <input
                id="ticket-now"
                type="radio"
                name="setLive"
                checked={liveNow}
                onChange={(event) => onLiveNowChange(event.target.checked)}
              />{' '}
              Now
            </ControlLabel>
          </div>
          <div className="col-sm-2">
            <ControlLabel>
              <input
                id="ticket-at"
                type="radio"
                name="setLive"
                checked={!liveNow}
                onChange={(event) => onLiveNowChange(!event.target.checked)}
              />{' '}
              At
            </ControlLabel>
          </div>
          <div className="col-sm-8">
            <DateTimePicker id="ticket-go-live" dt={goLiveTime} disabled={liveNow} onChange={onGoLiveTimeChange} />
          </div>
        </div>
      </div>

      {/* Due Date section */}
      <div className="form-group mar-top">
        <ControlLabel>
          <strong>Due</strong>
        </ControlLabel>
        <div className="row">
          <div className="col-sm-12">
            <DateTimePicker key={dueDateKey} id="ticket-due" dt={dueDate} onChange={setDueDate} />
          </div>
        </div>
      </div>

      {/* Notification checkbox */}
      <div className="form-group mar-top">
        <ControlLabel>
          <input
            id="ticket-notify"
            type="checkbox"
            onChange={(event) => setNotify(event.target.checked)}
            checked={notify}
          />{' '}
          <strong>Notify requestor and assignee on updates</strong>
        </ControlLabel>
      </div>

      {/* Images section */}
      <div className="form-group mar-top">
        <ControlLabel>
          <strong>Attachments</strong>
        </ControlLabel>
      </div>
      <Manager onSave={onUploaded}>
        {({ uploads }) => (
          <>
            {!!uploaded.length && (
              <div className="form-group">
                <Files>
                  {uploaded.map((entry) => (
                    <Files.Upload key={entry.url}>
                      <span>{entry.file.name}</span>{' '}
                      <a
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setUploaded(uploaded.filter(({ url }) => url !== entry.url));
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </a>
                    </Files.Upload>
                  ))}
                </Files>
              </div>
            )}
            {uploads.map((entry) => (
              <Uploader uploader={useLegacyAttachmentUpload} key={entry.uuid} {...entry} />
            ))}
            <div className="form-group">
              <div className="clearfix">
                <div className="pull-left">
                  <label className="btn btn-default">
                    <FontAwesomeIcon icon={faPaperclip} />
                    <Selector accept={ACCEPT} />
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </Manager>

      {/* Error message */}
      {formError && (
        <Text tag="p" style="danger" alignment="center">
          <strong>{formError}</strong>
        </Text>
      )}
    </>
  );

  const formButtons = (
    <>
      <Button kind="default" onClick={onClose} className="mr-2">
        Cancel
      </Button>
      <Button kind="primary" type="submit" loading={loading}>
        {ticket ? 'Update' : 'Create'}
      </Button>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={onSubmit}>
        {formContent}
        <div className="text-right mar-top">{formButtons}</div>
      </form>
    );
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        <Panel>
          <form onSubmit={onSubmit}>
            <Panel.Header>
              <Panel.Title>{ticket ? 'Edit Ticket' : 'New Ticket'}</Panel.Title>
            </Panel.Header>
            <Panel.Body>
              <div className="row">
                <div className="col-sm-8 col-sm-offset-2 col-md-6 col-md-offset-3">{formContent}</div>
              </div>
            </Panel.Body>

            <Panel.Footer align="right">{formButtons}</Panel.Footer>
          </form>
        </Panel>
      </div>
    </div>
  );
};
